import React, { useState, useEffect, useMemo } from 'react';
import {
    View,
    Text,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    SafeAreaView,
    KeyboardAvoidingView,
    Platform,
    Image,
    ActivityIndicator,
    Modal,
    ScrollView
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Lock, CheckCircle, AlertCircle, ChevronLeft } from 'lucide-react-native';
import PasswordStrengthIndicator, { passwordCriteria } from '@/components/PasswordStrengthIndicator';

export default function UpdatePasswordScreen() {
    const router = useRouter();
    const { oobCode, mode, continueUrl } = useLocalSearchParams<{ oobCode: string; mode?: string; continueUrl?: string }>();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [email, setEmail] = useState('');
    const [success, setSuccess] = useState(false);
    const [invalidCode, setInvalidCode] = useState(false);
    const { resetPassword, verifyResetCode, verifyEmailCode } = useAuth();
    const { colors } = useTheme();

    // Modal State
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState('');

    // Validações
    const isPasswordStrong = useMemo(() => {
        return passwordCriteria.every(criteria => criteria.test(password));
    }, [password]);

    const passwordsMatch = useMemo(() => {
        return password === confirmPassword && confirmPassword.length > 0;
    }, [password, confirmPassword]);

    const showPasswordMismatch = useMemo(() => {
        return confirmPassword.length > 0 && !passwordsMatch;
    }, [confirmPassword, passwordsMatch]);

    const isFormValid = useMemo(() => {
        return isPasswordStrong && passwordsMatch;
    }, [isPasswordStrong, passwordsMatch]);

    useEffect(() => {
        const verifyCode = async () => {
            if (!oobCode) {
                setInvalidCode(true);
                setVerifying(false);
                return;
            }

            try {
                if (mode === 'verifyEmail') {
                    await verifyEmailCode(oobCode);
                    setSuccess(true);
                } else {
                    const userEmail = await verifyResetCode(oobCode);
                    setEmail(userEmail);
                }
            } catch (error: any) {
                setInvalidCode(true);
                setErrorModalMessage(error.message || 'Código inválido.');
                setErrorModalVisible(true);
            } finally {
                setVerifying(false);
            }
        };

        verifyCode();
    }, [oobCode, mode]);

    const getSafeContinuePath = () => {
        if (!continueUrl) return null;
        if (typeof continueUrl !== 'string') return null;
        try {
            const url = new URL(continueUrl);
            const allowedHosts = new Set(['poenoracha.com.br', 'www.poenoracha.com.br']);
            if (!allowedHosts.has(url.hostname)) return null;
            return `${url.pathname}${url.search}${url.hash}`;
        } catch {
            return null;
        }
    };

    const showErrorModal = (message: string) => {
        setErrorModalMessage(message);
        setErrorModalVisible(true);
    };

    const handleResetPassword = async () => {
        if (!isFormValid) {
            showErrorModal('Por favor, preencha todos os campos corretamente.');
            return;
        }

        if (!oobCode) {
            showErrorModal('Código de redefinição não encontrado.');
            return;
        }

        setLoading(true);
        try {
            await resetPassword(oobCode, password);
            setSuccess(true);
        } catch (error: any) {
            showErrorModal(error.message || 'Erro ao redefinir senha.');
        } finally {
            setLoading(false);
        }
    };

    const styles = StyleSheet.create({
        container: {
            flex: 1,
            backgroundColor: colors.background,
        },
        backButton: {
            position: 'absolute',
            top: 24,
            left: 24,
            padding: 12,
            borderRadius: 24,
            zIndex: 10,
        },
        keyboardView: {
            flex: 1,
        },
        scrollView: {
            flex: 1,
        },
        content: {
            flex: 1,
            paddingHorizontal: 24,
            justifyContent: 'center',
            minHeight: '100%',
        },
        loadingContainer: {
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
        },
        loadingText: {
            marginTop: 16,
            fontSize: 16,
            fontFamily: 'Inter-Regular',
            color: colors.textSecondary,
        },
        header: {
            alignItems: 'center',
            marginBottom: 48,
        },
        logo: {
            width: 80,
            height: 80,
            marginBottom: 16,
        },
        title: {
            fontSize: 28,
            fontFamily: 'Inter-Bold',
            color: colors.text,
            marginBottom: 8,
        },
        subtitle: {
            fontSize: 16,
            fontFamily: 'Inter-Regular',
            color: colors.textSecondary,
            textAlign: 'center',
            lineHeight: 24,
        },
        emailText: {
            fontSize: 14,
            fontFamily: 'Inter-SemiBold',
            color: colors.primary,
            marginTop: 8,
        },
        form: {
            gap: 16,
        },
        inputContainer: {
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: colors.surface,
            borderRadius: 12,
            paddingHorizontal: 16,
            height: 56,
            borderWidth: 1,
            borderColor: colors.border,
        },
        inputIcon: {
            marginRight: 12,
        },
        input: {
            flex: 1,
            fontSize: 16,
            fontFamily: 'Inter-Regular',
            color: colors.text,
        },
        submitButton: {
            backgroundColor: colors.primary,
            borderRadius: 12,
            height: 56,
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 8,
        },
        submitButtonDisabled: {
            opacity: 0.6,
        },
        submitButtonText: {
            fontSize: 16,
            fontFamily: 'Inter-SemiBold',
            color: '#FFFFFF',
        },
        errorContainer: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            marginTop: 8,
        },
        errorText: {
            fontSize: 12,
            fontFamily: 'Inter-Regular',
            color: '#EF4444',
        },
        // Success/Error State
        stateContainer: {
            alignItems: 'center',
            gap: 16,
        },
        stateIcon: {
            marginBottom: 8,
        },
        stateTitle: {
            fontSize: 24,
            fontFamily: 'Inter-Bold',
            color: colors.text,
            textAlign: 'center',
        },
        stateMessage: {
            fontSize: 16,
            fontFamily: 'Inter-Regular',
            color: colors.textSecondary,
            textAlign: 'center',
            lineHeight: 24,
            paddingHorizontal: 16,
        },
        actionButton: {
            backgroundColor: colors.primary,
            borderRadius: 12,
            height: 56,
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            marginTop: 16,
        },
        // Modal Styles
        modalOverlay: {
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 24,
        },
        modalContent: {
            width: '100%',
            maxWidth: 320,
            borderRadius: 24,
            padding: 24,
            alignItems: 'center',
        },
        modalIconContainer: {
            marginBottom: 16,
        },
        modalTitle: {
            fontSize: 20,
            fontFamily: 'Inter-Bold',
            marginBottom: 8,
            textAlign: 'center',
        },
        modalMessage: {
            fontSize: 14,
            fontFamily: 'Inter-Regular',
            textAlign: 'center',
            marginBottom: 24,
            lineHeight: 20,
        },
        modalButton: {
            width: '100%',
            paddingVertical: 12,
            borderRadius: 12,
            alignItems: 'center',
        },
        modalButtonText: {
            fontSize: 16,
            fontFamily: 'Inter-SemiBold',
            color: '#FFFFFF',
        },
    });

    // Loading verification
    if (verifying) {
        return (
            <SafeAreaView style={styles.container}>
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={colors.primary} />
                    <Text style={styles.loadingText}>Verificando link...</Text>
                </View>
            </SafeAreaView>
        );
    }

    // Invalid code
    if (invalidCode) {
        return (
            <SafeAreaView style={styles.container}>
                <View style={styles.content}>
                    <View style={styles.stateContainer}>
                        <AlertCircle size={64} color={colors.error} style={styles.stateIcon} />
                        <Text style={styles.stateTitle}>Link Inválido</Text>
                        <Text style={styles.stateMessage}>
                            Este link é inválido ou expirou. Por favor, solicite um novo.
                        </Text>
                        <TouchableOpacity
                            style={styles.actionButton}
                            onPress={() => router.push('/(auth)/login')}
                        >
                            <Text style={styles.submitButtonText}>Ir para Login</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </SafeAreaView>
        );
    }

    // Success
    if (success) {
        if (mode === 'verifyEmail') {
            const nextPath = getSafeContinuePath() || '/login';
            return (
                <SafeAreaView style={styles.container}>
                    <View style={styles.content}>
                        <View style={styles.stateContainer}>
                            <CheckCircle size={64} color={colors.success} style={styles.stateIcon} />
                            <Text style={styles.stateTitle}>E-mail Verificado!</Text>
                            <Text style={styles.stateMessage}>
                                Seu e-mail foi verificado com sucesso. Agora você pode fazer login.
                            </Text>
                            <TouchableOpacity
                                style={styles.actionButton}
                                onPress={() => router.replace(nextPath as any)}
                            >
                                <Text style={styles.submitButtonText}>Continuar</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </SafeAreaView>
            );
        }
        return (
            <SafeAreaView style={styles.container}>
                <View style={styles.content}>
                    <View style={styles.stateContainer}>
                        <CheckCircle size={64} color={colors.success} style={styles.stateIcon} />
                        <Text style={styles.stateTitle}>Senha Redefinida!</Text>
                        <Text style={styles.stateMessage}>
                            Sua senha foi alterada com sucesso. Agora você pode fazer login com a nova senha.
                        </Text>
                        <TouchableOpacity
                            style={styles.actionButton}
                            onPress={() => router.push('/(auth)/login')}
                        >
                            <Text style={styles.submitButtonText}>Ir para Login</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.container}>
            <TouchableOpacity
                style={[styles.backButton, { backgroundColor: colors.surface }]}
                activeOpacity={0.7}
                onPress={() => router.push('/(auth)/login')}
            >
                <ChevronLeft size={20} color={colors.text} />
            </TouchableOpacity>
            <KeyboardAvoidingView
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                style={styles.keyboardView}
            >
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={{ flexGrow: 1 }}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.content}>
                        <View style={styles.header}>
                            <Image
                                source={require('@/public/icon-pages.png')}
                                style={styles.logo}
                                resizeMode="contain"
                            />
                            <Text style={styles.title}>Nova Senha</Text>
                            <Text style={styles.subtitle}>
                                Crie uma nova senha para sua conta
                            </Text>
                            {email && <Text style={styles.emailText}>{email}</Text>}
                        </View>

                        <View style={styles.form}>
                            <View style={styles.inputContainer}>
                                <Lock size={20} color={colors.textSecondary} style={styles.inputIcon} />
                                <TextInput
                                    style={styles.input}
                                    placeholder="Nova Senha"
                                    placeholderTextColor={colors.textSecondary}
                                    value={password}
                                    onChangeText={setPassword}
                                    secureTextEntry
                                    autoCapitalize="none"
                                />
                            </View>

                            {password.length > 0 && (
                                <PasswordStrengthIndicator password={password} />
                            )}

                            <View style={styles.inputContainer}>
                                <Lock size={20} color={colors.textSecondary} style={styles.inputIcon} />
                                <TextInput
                                    style={styles.input}
                                    placeholder="Confirmar Nova Senha"
                                    placeholderTextColor={colors.textSecondary}
                                    value={confirmPassword}
                                    onChangeText={setConfirmPassword}
                                    secureTextEntry
                                    autoCapitalize="none"
                                />
                            </View>

                            {showPasswordMismatch && (
                                <View style={styles.errorContainer}>
                                    <AlertCircle size={16} color="#EF4444" />
                                    <Text style={styles.errorText}>As senhas não coincidem</Text>
                                </View>
                            )}

                            <TouchableOpacity
                                style={[
                                    styles.submitButton,
                                    (!isFormValid || loading) && styles.submitButtonDisabled,
                                ]}
                                onPress={handleResetPassword}
                                disabled={!isFormValid || loading}
                            >
                                {loading ? (
                                    <ActivityIndicator color="#FFFFFF" />
                                ) : (
                                    <Text style={styles.submitButtonText}>Redefinir Senha</Text>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>
                </ScrollView>
            </KeyboardAvoidingView>

            {/* Error Modal */}
            <Modal
                animationType="fade"
                transparent={true}
                visible={errorModalVisible}
                onRequestClose={() => setErrorModalVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={[styles.modalContent, { backgroundColor: colors.surface }]}>
                        <View style={styles.modalIconContainer}>
                            <AlertCircle size={48} color={colors.error} />
                        </View>
                        <Text style={[styles.modalTitle, { color: colors.text }]}>Erro</Text>
                        <Text style={[styles.modalMessage, { color: colors.textSecondary }]}>
                            {errorModalMessage}
                        </Text>
                        <TouchableOpacity
                            style={[styles.modalButton, { backgroundColor: colors.primary }]}
                            onPress={() => setErrorModalVisible(false)}
                        >
                            <Text style={styles.modalButtonText}>OK</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </Modal>
        </SafeAreaView>
    );
}
